import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import GameShell from "@/components/GameShell";
import BetControls from "@/components/BetControls";
import { api, formatAmount } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useCurrency } from "@/lib/currency";

export default function Crash() {
  const { setUser, user } = useAuth();
  const { currency } = useCurrency();
  const [amount, setAmount] = useState(10);
  const [target, setTarget] = useState(2.0);
  const [busy, setBusy] = useState(false);
  const [history, setHistory] = useState([]);
  const [live, setLive] = useState(null); // {status, multiplier, final, won}
  const timer = useRef();

  useEffect(() => {
    const t = timer;
    return () => clearInterval(t.current);
  }, []);

  const autoPlaceBet = async (betAmount) => {
    try {
      const { data } = await api.post("/games/crash", {
        amount: Number(betAmount),
        currency,
        target_multiplier: Number(target),
      });
      const { crash_point: crash, won } = data.bet.result;
      setLive({ status: "done", multiplier: crash, final: crash, won });
      setUser((u) => ({ ...u, balances: data.balances }));
      setHistory((h) => [{ crash, won, id: data.bet.id, profit: data.bet.profit, currency }, ...h].slice(0, 20));
      return data.bet;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
      return null;
    }
  };

  const play = async () => {
    if (!user) return;
    if (Number(amount) <= 0) return toast.error("Enter a bet");
    setBusy(true);
    setLive({ status: "running", multiplier: 1.0 });
    try {
      const { data } = await api.post("/games/crash", {
        amount: Number(amount),
        currency,
        target_multiplier: Number(target),
      });
      // Animate up to crash_point
      const crash = data.bet.result.crash_point;
      const won = data.bet.result.won;
      const dur = Math.min(5000, 1500 + Math.log(crash + 1) * 1000);
      const start = performance.now();
      clearInterval(timer.current);
      timer.current = setInterval(() => {
        const t = (performance.now() - start) / dur;
        if (t >= 1) {
          clearInterval(timer.current);
          setLive({ status: "done", multiplier: crash, final: crash, won });
          setUser((u) => ({ ...u, balances: data.balances }));
          setHistory((h) => [{ crash, won, id: data.bet.id, profit: data.bet.profit, currency }, ...h].slice(0, 20));
          setBusy(false);
          if (won) toast.success(`Cashed at ×${Number(target).toFixed(2)} · +${formatAmount(data.bet.profit, currency)}`);
          else toast.error(`Crashed at ×${crash.toFixed(2)}`);
          return;
        }
        const cur = 1 + (crash - 1) * Math.pow(t, 1.8);
        setLive({ status: "running", multiplier: cur });
      }, 50);
    } catch (e) {
      setBusy(false);
      setLive(null);
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const multDisplay = live ? Number(live.multiplier).toFixed(2) : "1.00";

  return (
    <GameShell title="Crash" subtitle="Set your target. Cash out before gravity wins." accent="#00F0FF"
      aside={
        <BetControls
          amount={amount}
          setAmount={setAmount}
          onPlay={play}
          autoPlaceBet={autoPlaceBet}
          busy={busy}
          disabled={!user || busy}
          playLabel={`Bet @ ×${Number(target).toFixed(2)}`}
          extra={
            <div>
              <div className="flex items-center justify-between mb-2">
                <span className="text-[10px] uppercase tracking-widest text-zinc-500">Target Multiplier</span>
                <span className="text-[10px] text-zinc-500 font-mono-num">Win chance ≈ {(99 / Number(target || 1)).toFixed(2)}%</span>
              </div>
              <div className="panel flex items-center gap-2 px-3 py-2" style={{ background: "#0F0F11" }}>
                <input
                  type="number"
                  min="1.01"
                  step="0.01"
                  value={target}
                  onChange={(e) => setTarget(e.target.value)}
                  className="flex-1 bg-transparent outline-none text-right font-mono-num text-lg"
                  data-testid="crash-target"
                />
                <span className="text-xs text-zinc-500 font-mono-num">×</span>
              </div>
            </div>
          }
        />
      }
    >
      <div className="h-[460px] flex flex-col items-center justify-center relative">
        <div
          className="relative font-display font-black tracking-tighter crash-live"
          style={{
            fontSize: "7rem",
            color: live?.status === "done" && !live.won ? "#FF3366" : "#00F0FF",
          }}
          data-testid="crash-multiplier"
        >
          ×{multDisplay}
        </div>
        <div className="text-xs uppercase tracking-widest text-zinc-500 mt-1">
          {live?.status === "running" ? "In flight…" : live?.status === "done" ? (live.won ? "Cashed out" : "Collapsed") : "Awaiting liftoff"}
        </div>

        {/* Simple SVG curve */}
        <svg viewBox="0 0 400 180" className="absolute bottom-6 left-6 right-6 h-40 w-[calc(100%-48px)] opacity-80 pointer-events-none" preserveAspectRatio="none">
          <defs>
            <linearGradient id="curve" x1="0" x2="1" y1="0" y2="0">
              <stop offset="0" stopColor="#00F0FF" stopOpacity="0" />
              <stop offset="1" stopColor="#00F0FF" stopOpacity="1" />
            </linearGradient>
          </defs>
          {live && (
            <path
              d={`M0 180 Q 200 ${180 - Math.min(170, (Number(live.multiplier) - 1) * 40)} 400 ${180 - Math.min(170, (Number(live.multiplier) - 1) * 60)}`}
              stroke="url(#curve)"
              strokeWidth="2.5"
              fill="none"
            />
          )}
        </svg>
      </div>

      <div className="flex items-center gap-2 flex-wrap mt-3" data-testid="crash-history">
        {history.map((h, i) => (
          <span
            key={h.id}
            className={`chip font-mono-num ${h.won ? "text-emerald-400 border-emerald-500/30" : "text-rose-400 border-rose-500/30"}`}
            style={{ opacity: 1 - i * 0.03 }}
          >
            ×{Number(h.crash).toFixed(2)}
          </span>
        ))}
      </div>
    </GameShell>
  );
}
